import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiCalendar, FiActivity, FiMessageSquare, FiTrendingUp,
  FiPlus, FiChevronLeft, FiChevronRight, FiX, FiBarChart2,
  FiClock, FiTarget, FiStar, FiEdit, FiEye, FiSearch, FiBell,
  FiDollarSign, FiAlertCircle, FiCheckCircle,
  FiSettings, FiLogOut, FiAward, FiCoffee
} from 'react-icons/fi';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { collection, query, where, getDocs, orderBy, limit, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Calendar from './Calendar';
import Programs from '../Programs/Programs';
import Feedback from '../Feedback/Feedback';
import Analytics from './Analytics';
import NutritionManagement from './NutritionManagement';
import NutritionOverview from './NutritionOverview';
import ClientNutritionStatus from './ClientNutritionStatus';
import NutritionQuickTools from './NutritionQuickTools';
import './TrainerDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const TrainerDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // Firestore data state
  const [clients, setClients] = useState([]);
  const [clientFeedback, setClientFeedback] = useState([]);
  const [progressData, setProgressData] = useState({});
  const [trainerSchedule, setTrainerSchedule] = useState([]);
  const [workoutPrograms, setWorkoutPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [selectedClient, setSelectedClient] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeWidget, setActiveWidget] = useState('overview');
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'
  const [showProgramCreator, setShowProgramCreator] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [statsPeriod, setStatsPeriod] = useState('month'); // 'today', 'week', 'month'
  const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'feedback', 'progress', 'sessions'
  const [notifications] = useState(0); // Notification count

  // Invitation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Program creator state
  const [newProgram, setNewProgram] = useState({
    name: '',
    category: 'Push',
    exercises: []
  });

  const [newExercise, setNewExercise] = useState({
    name: '',
    sets: 3,
    reps: 10,
    weight: 0,
    rest: 60
  });

  // Calendar state
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    type: 'session',
    date: new Date(),
    startTime: '09:00',
    endTime: '10:00',
    clientId: null,
    clientName: '',
    location: '',
    notes: ''
  });
  const [eventSaving, setEventSaving] = useState(false);

  // Fetch trainer's clients from Firestore
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;

      try {
        console.log('[TrainerDashboard] Fetching clients for trainer:', currentUser.uid);
        const usersRef = collection(db, 'users');
        const clientsQuery = query(
          usersRef,
          where('trainerId', '==', currentUser.uid),
          where('role', '==', 'client')
        );

        const querySnapshot = await getDocs(clientsQuery);
        const clientsData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          clientsData.push({
            id: doc.id,
            name: data.displayName || 'Unnamed Client',
            email: data.email,
            phone: data.phoneNumber || 'No phone',
            avatar: '👤',
            status: data.isActive ? 'active' : 'inactive',
            stage: 'Active',
            goals: data.goals || [],
            compliance: 0,
            nextSession: 'Not scheduled'
          });
        });

        console.log('[TrainerDashboard] Fetched clients:', clientsData.length);
        setClients(clientsData);
        if (clientsData.length > 0) {
          setSelectedClient(clientsData[0]);
        }
      } catch (error) {
        console.error('[TrainerDashboard] Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [currentUser]);

  // Fetch client feedback
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!currentUser) return;

      try {
        console.log('[TrainerDashboard] Fetching feedback for trainer:', currentUser.uid);
        const feedbackRef = collection(db, 'clientFeedback');
        const feedbackQuery = query(
          feedbackRef,
          where('trainerId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(10)
        );

        const querySnapshot = await getDocs(feedbackQuery);
        const feedbackData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          feedbackData.push({
            id: doc.id,
            clientId: data.userId,
            clientName: 'Client', // We'll need to fetch this from users collection
            rating: data.rating || 0,
            message: data.feedback || '',
            date: data.createdAt?.toDate().toLocaleDateString() || 'Recent',
            type: 'feedback'
          });
        });

        console.log('[TrainerDashboard] Fetched feedback:', feedbackData.length);
        setClientFeedback(feedbackData);
      } catch (error) {
        console.error('[TrainerDashboard] Error fetching feedback:', error);
      }
    };

    fetchFeedback();
  }, [currentUser]);

  // Fetch workout programs
  useEffect(() => {
    const fetchPrograms = async () => {
      if (!currentUser) return;

      try {
        console.log('[TrainerDashboard] Fetching programs for trainer:', currentUser.uid);
        const programsRef = collection(db, 'workoutPrograms');
        const programsQuery = query(
          programsRef,
          where('trainerId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(programsQuery);
        const programsData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          programsData.push({
            id: doc.id,
            name: data.name || 'Unnamed Program',
            description: data.description || '',
            duration: data.duration || 0,
            exercises: data.exercises || [],
            assignedClients: data.assignedClients || [],
            isPublic: data.isPublic || false,
            createdAt: data.createdAt?.toDate() || new Date()
          });
        });

        console.log('[TrainerDashboard] Fetched programs:', programsData.length);
        setWorkoutPrograms(programsData);
      } catch (error) {
        console.error('[TrainerDashboard] Error fetching programs:', error);
      }
    };

    fetchPrograms();
  }, [currentUser]);

  // Fetch pending invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!currentUser) return;

      try {
        console.log('[TrainerDashboard] Fetching invitations for trainer:', currentUser.uid);
        const invitationsRef = collection(db, 'clientInvitations');
        const invitationsQuery = query(
          invitationsRef,
          where('trainerId', '==', currentUser.uid),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(invitationsQuery);
        const invitationsData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          invitationsData.push({
            id: doc.id,
            email: data.email,
            inviteCode: data.inviteCode,
            createdAt: data.createdAt?.toDate() || new Date(),
            expiresAt: data.expiresAt?.toDate() || new Date()
          });
        });

        console.log('[TrainerDashboard] Fetched invitations:', invitationsData.length);
        setPendingInvitations(invitationsData);
      } catch (error) {
        console.error('[TrainerDashboard] Error fetching invitations:', error);
      }
    };

    fetchInvitations();
  }, [currentUser]);

  // Fetch calendar entries
  useEffect(() => {
    const fetchCalendarEntries = async () => {
      if (!currentUser) return;

      try {
        console.log('[TrainerDashboard] Fetching calendar entries for trainer:', currentUser.uid);
        const entriesRef = collection(db, 'calendarEntries');
        const entriesQuery = query(
          entriesRef,
          where('trainerId', '==', currentUser.uid),
          orderBy('date', 'asc')
        );

        const querySnapshot = await getDocs(entriesQuery);
        const entriesData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entriesData.push({
            id: doc.id,
            ...data,
            date: data.date?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });

        console.log('[TrainerDashboard] Fetched calendar entries:', entriesData.length);
        setCalendarEntries(entriesData);
      } catch (error) {
        console.error('[TrainerDashboard] Error fetching calendar entries:', error);
      }
    };

    fetchCalendarEntries();
  }, [currentUser]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Coach data from userProfile
  const coachData = {
    name: userProfile?.displayName || "Coach",
    avatar: "👨‍🏫",
    totalClients: clients.length,
    monthlyRevenue: 0, // TODO: Calculate from subscriptions
    revenueChange: 0,
    completionRate: 0,
    satisfaction: clientFeedback.length > 0
      ? (clientFeedback.reduce((sum, f) => sum + (f.rating || 0), 0) / clientFeedback.length).toFixed(1)
      : 0
  };

  // Motivational quotes
  const motivationalQuotes = [
    "Every champion was once a contender that refused to give up.",
    "Success isn't given. It's earned in the gym.",
    "The only bad workout is the one that didn't happen.",
    "Your clients' success is your success."
  ];

  const todaysQuote = motivationalQuotes[new Date().getDate() % motivationalQuotes.length];

  // Revenue chart data - TODO: Calculate from actual subscription data
  const revenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Revenue',
        data: [0, 0, 0, 0, 0, 0], // Will be calculated from subscriptions
        borderColor: '#ff6b35',
        backgroundColor: 'rgba(255, 107, 53, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(10, 10, 10, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ff6b35',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6
      }
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'clients', label: 'Clients', icon: FiUsers },
    { id: 'calendar', label: 'Calendar', icon: FiCalendar },
    { id: 'programs', label: 'Programs', icon: FiActivity },
    { id: 'nutrition', label: 'Nutrition', icon: FiCoffee },
    { id: 'feedback', label: 'Feedback', icon: FiMessageSquare },
    { id: 'analytics', label: 'Analytics', icon: FiTrendingUp }
  ];

  const addExerciseToProgram = () => {
    if (newExercise.name) {
      setNewProgram(prev => ({
        ...prev,
        exercises: [...prev.exercises, { ...newExercise, id: Date.now() }]
      }));
      setNewExercise({ name: '', sets: 3, reps: 10, weight: 0, rest: 60 });
    }
  };

  // Generate unique invitation code
  const generateInviteCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Handle client invitation
  const handleInviteClient = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setInviteSending(true);

    try {
      const inviteCode = generateInviteCode();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      // Create invitation document
      const invitationData = {
        trainerId: currentUser.uid,
        trainerName: userProfile?.displayName || 'Your Coach',
        trainerEmail: currentUser.email,
        email: inviteEmail,
        inviteCode: inviteCode,
        message: inviteMessage || `Join my coaching program!`,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: expiresAt
      };

      const docRef = await addDoc(collection(db, 'clientInvitations'), invitationData);
      console.log('[Invite] Invitation created:', docRef.id);

      // Generate invitation link
      const inviteLink = `${window.location.origin}/accept-invite/${inviteCode}`;
      setGeneratedLink(inviteLink);

      // Refresh invitations list
      setPendingInvitations(prev => [...prev, {
        id: docRef.id,
        email: inviteEmail,
        inviteCode: inviteCode,
        createdAt: new Date(),
        expiresAt: expiresAt
      }]);

      alert(`Invitation sent! Share this link with your client:\n\n${inviteLink}`);

      // Reset form
      setInviteEmail('');
      setInviteMessage('');
    } catch (error) {
      console.error('[Invite] Error creating invitation:', error);
      alert('Error creating invitation. Please try again.');
    } finally {
      setInviteSending(false);
    }
  };

  // Copy invitation link to clipboard
  const copyInviteLink = (inviteCode) => {
    const link = `${window.location.origin}/accept-invite/${inviteCode}`;
    navigator.clipboard.writeText(link);
    alert('Invitation link copied to clipboard!');
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      return (
        entryDate.getFullYear() === date.getFullYear() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getDate() === date.getDate()
      );
    });
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setNewEvent({ ...newEvent, date });
    setShowEventModal(true);
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.type) {
      alert('Please provide a title and event type');
      return;
    }

    setEventSaving(true);

    try {
      const eventData = {
        trainerId: currentUser.uid,
        trainerName: userProfile?.displayName || 'Coach',
        title: newEvent.title,
        description: newEvent.description,
        type: newEvent.type,
        date: newEvent.date,
        startTime: newEvent.startTime,
        endTime: newEvent.endTime,
        duration: calculateDuration(newEvent.startTime, newEvent.endTime),
        clientId: newEvent.clientId || null,
        clientName: newEvent.clientName || null,
        location: newEvent.location,
        notes: newEvent.notes,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'calendarEntries'), eventData);

      // Add to local state
      setCalendarEntries([...calendarEntries, { id: docRef.id, ...eventData, date: newEvent.date, createdAt: new Date(), updatedAt: new Date() }]);

      // Reset form
      setNewEvent({
        title: '',
        description: '',
        type: 'session',
        date: new Date(),
        startTime: '09:00',
        endTime: '10:00',
        clientId: null,
        clientName: '',
        location: '',
        notes: ''
      });
      setShowEventModal(false);
      alert('Event created successfully!');
    } catch (error) {
      console.error('[Calendar] Error creating event:', error);
      alert('Error creating event. Please try again.');
    } finally {
      setEventSaving(false);
    }
  };

  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes - startMinutes;
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await deleteDoc(doc(db, 'calendarEntries', eventId));
      setCalendarEntries(calendarEntries.filter(e => e.id !== eventId));
      alert('Event deleted successfully!');
    } catch (error) {
      console.error('[Calendar] Error deleting event:', error);
      alert('Error deleting event. Please try again.');
    }
  };

  const getClientProgress = (clientId) => {
    const data = progressData[clientId];
    if (!data || !data.weight || data.weight.length < 2) return { trend: 'stable', change: 0 };
    
    const latest = data.weight[data.weight.length - 1].value;
    const previous = data.weight[data.weight.length - 2].value;
    const change = latest - previous;
    const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'stable';
    
    return { trend, change: Math.abs(change) };
  };

  const renderOverview = () => (
    <div className="enhanced-overview">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="greeting-section">
            <h1>Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {coachData.name}! 👋</h1>
            <p className="motivational-quote">"{todaysQuote}"</p>
            <div className="date-time">
              <FiClock />
              <span>{currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span>{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <div className="search-container">
            <FiSearch />
            <input 
              type="text" 
              placeholder="Search clients, programs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="notification-bell">
            <FiBell />
            {notifications > 0 && <span className="notification-count">{notifications}</span>}
          </div>
          <div className="period-selector">
            <select value={statsPeriod} onChange={(e) => setStatsPeriod(e.target.value)}>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="enhanced-stats-grid">
        <div className="enhanced-stat-card clients">
          <div className="stat-header">
            <div className="stat-icon">
              <FiUsers />
            </div>
            <div className="trend-indicator positive">
              <FiTrendingUp />
              <span>+2</span>
            </div>
          </div>
          <div className="stat-main">
            <h2>{coachData.totalClients}</h2>
            <p>Active Clients</p>
          </div>
          <div className="mini-chart">
            <div className="sparkline clients-sparkline"></div>
          </div>
        </div>

        <div className="enhanced-stat-card revenue">
          <div className="stat-header">
            <div className="stat-icon">
              <FiDollarSign />
            </div>
            <div className="trend-indicator positive">
              <FiTrendingUp />
              <span>+{coachData.revenueChange}%</span>
            </div>
          </div>
          <div className="stat-main">
            <h2>R{coachData.monthlyRevenue.toLocaleString()}</h2>
            <p>Monthly Revenue</p>
          </div>
          <div className="mini-chart">
            <Line data={revenueData} options={chartOptions} height={40} />
          </div>
        </div>

        <div className="enhanced-stat-card sessions">
          <div className="stat-header">
            <div className="stat-icon">
              <FiCalendar />
            </div>
            <div className="trend-indicator neutral">
              <FiTarget />
              <span>{trainerSchedule.length}/0</span>
            </div>
          </div>
          <div className="stat-main">
            <h2>{trainerSchedule.length}</h2>
            <p>Today's Sessions</p>
          </div>
          <div className="completion-bar">
            <div className="completion-fill" style={{width: trainerSchedule.length > 0 ? '100%' : '0%'}}></div>
          </div>
        </div>

        <div className="enhanced-stat-card satisfaction">
          <div className="stat-header">
            <div className="stat-icon">
              <FiStar />
            </div>
            <div className="trend-indicator positive">
              <FiAward />
              <span>{clientFeedback.length > 0 ? 'Rated' : 'N/A'}</span>
            </div>
          </div>
          <div className="stat-main">
            <h2>{coachData.satisfaction > 0 ? coachData.satisfaction : 'N/A'}</h2>
            <p>Client Satisfaction</p>
          </div>
          <div className="rating-stars">
            {[1,2,3,4,5].map(i => (
              <FiStar key={i} className={i <= parseFloat(coachData.satisfaction) ? 'filled' : ''} />
            ))}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="overview-content-grid">
        {/* Left Column */}
        <div className="left-column">
          {/* Today's Schedule */}
          <div className="today-schedule-card">
            <div className="card-header">
              <h3>Today's Schedule</h3>
              <button className="quick-action-btn" onClick={() => setActiveWidget('calendar')}>
                <FiPlus />
                Add Session
              </button>
            </div>
            <div className="schedule-list">
              {trainerSchedule.length === 0 && (
                <div className="no-sessions">
                  <FiCalendar />
                  <p>No sessions scheduled for today</p>
                  <button onClick={() => setActiveWidget('calendar')}>Schedule Session</button>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="revenue-chart-card">
            <div className="card-header">
              <h3>Revenue Trend</h3>
              <div className="revenue-summary">
                <span className="current-month">R{coachData.monthlyRevenue.toLocaleString()}</span>
                <span className="change positive">+{coachData.revenueChange}%</span>
              </div>
            </div>
            <div className="chart-container">
              <Line data={revenueData} options={{
                ...chartOptions,
                scales: {
                  x: { 
                    display: true,
                    ticks: { color: 'rgba(255, 255, 255, 0.7)' },
                    grid: { display: false }
                  },
                  y: {
                    display: true,
                    ticks: {
                      color: 'rgba(255, 255, 255, 0.7)',
                      callback: function(value) {
                        return 'R' + value;
                      }
                    },
                    grid: { color: 'rgba(255, 255, 255, 0.1)' }
                  }
                }
              }} height={200} />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Client Alerts */}
          <div className="client-alerts-card">
            <div className="card-header">
              <h3>Client Alerts</h3>
              <div className="alert-summary">
                <span className="alert-count">0</span>
                <span>Need Attention</span>
              </div>
            </div>
            <div className="alerts-list">
              {clients.length === 0 ? (
                <div className="no-alerts">
                  <FiAlertCircle />
                  <p>No client alerts yet</p>
                </div>
              ) : (
                <div className="no-alerts">
                  <FiCheckCircle />
                  <p>All clients are up to date</p>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Recent Activity */}
          <div className="recent-activity-enhanced">
            <div className="card-header">
              <h3>Recent Activity</h3>
              <div className="activity-filters">
                <button 
                  className={`filter-btn ${activityFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn ${activityFilter === 'feedback' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('feedback')}
                >
                  Feedback
                </button>
                <button 
                  className={`filter-btn ${activityFilter === 'progress' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('progress')}
                >
                  Progress
                </button>
              </div>
            </div>
            <div className="activity-timeline">
              {clientFeedback.length === 0 ? (
                <div className="no-activity">
                  <FiMessageSquare />
                  <p>No recent activity to display</p>
                </div>
              ) : (
                clientFeedback.slice(0, 4).map((feedback, index) => (
                  <div key={feedback.id} className="timeline-item">
                    <div className="timeline-marker">
                      <div className={`activity-type-icon ${feedback.type}`}>
                        {feedback.type === 'feedback' && <FiMessageSquare />}
                        {feedback.type === 'progress' && <FiTrendingUp />}
                        {feedback.type === 'question' && <FiAlertCircle />}
                      </div>
                    </div>
                    <div className="timeline-content">
                      <div className="activity-header">
                        <div className="client-info">
                          <span className="client-avatar">👤</span>
                          <strong>{feedback.clientName}</strong>
                          <span className="activity-type">{feedback.type}</span>
                        </div>
                        <span className="activity-time">{feedback.date}</span>
                      </div>
                      <p className="activity-message">{feedback.message.substring(0, 80)}...</p>
                      <div className="activity-actions">
                        <button className="action-btn-small view">
                          <FiEye />
                          View
                        </button>
                        <button className="action-btn-small respond">
                          <FiMessageSquare />
                          Respond
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Nutrition Section */}
      <div className="nutrition-widgets-section">
        <h3 className="section-title">Nutrition Management</h3>
        <div className="nutrition-widgets-grid">
          <NutritionOverview
            coachId={currentUser.uid}
            onCreatePlan={() => setActiveWidget('nutrition')}
            onViewReports={() => setActiveWidget('nutrition')}
          />
          <NutritionQuickTools
            onMacroCalculator={() => setActiveWidget('nutrition')}
            onCreatePlan={() => setActiveWidget('nutrition')}
            onBuildMealPlan={() => setActiveWidget('nutrition')}
            onAddRecipe={() => setActiveWidget('nutrition')}
          />
        </div>
        <ClientNutritionStatus
          clients={clients}
          coachId={currentUser.uid}
          onViewPlan={() => setActiveWidget('nutrition')}
          onViewLogs={() => setActiveWidget('nutrition')}
          onMessageClient={(client) => console.log('Message', client)}
        />
      </div>

      {/* Quick Tasks Section */}
      <div className="quick-tasks-section">
        <div className="card-header">
          <h3>Quick Tasks</h3>
          <span className="tasks-count">0 pending</span>
        </div>
        <div className="tasks-grid">
          <div className="no-tasks">
            <FiCheckCircle />
            <p>No pending tasks</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderClients = () => {
    return (
      <div className="clients-section-modern">
        {/* Header */}
        <div className="clients-header-modern">
          <div className="header-left">
            <h2>Client Management</h2>
            <p className="header-subtitle">{clients.length} active client{clients.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Invitation Modal */}
        {showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Invite a New Client</h3>
                <button className="modal-close" onClick={() => setShowInviteModal(false)}>
                  <FiX />
                </button>
              </div>

              <div className="modal-body">
                <p className="modal-description">
                  Send an invitation link to your client. They'll be able to sign up and automatically connect to your coaching program.
                </p>

                <div className="form-group">
                  <label htmlFor="inviteEmail">Client Email *</label>
                  <input
                    type="email"
                    id="inviteEmail"
                    placeholder="client@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="modern-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="inviteMessage">Personal Message (optional)</label>
                  <textarea
                    id="inviteMessage"
                    placeholder="Add a personal message to your invitation..."
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    className="modern-textarea"
                    rows="3"
                  />
                </div>

                {generatedLink && (
                  <div className="generated-link-box">
                    <label>Invitation Link</label>
                    <div className="link-container">
                      <input
                        type="text"
                        value={generatedLink}
                        readOnly
                        className="link-input"
                      />
                      <button
                        className="copy-btn"
                        onClick={() => copyInviteLink(generatedLink.split('/').pop())}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowInviteModal(false);
                    setGeneratedLink('');
                    setInviteEmail('');
                    setInviteMessage('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleInviteClient}
                  disabled={inviteSending || !inviteEmail}
                >
                  {inviteSending ? 'Sending...' : 'Generate Invite Link'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="pending-invitations">
            <div className="pending-header">
              <h3>Pending Invitations ({pendingInvitations.length})</h3>
              <button className="invite-btn-primary" onClick={() => setShowInviteModal(true)}>
                <FiPlus />
                Invite New Client
              </button>
            </div>
            <div className="invitations-list">
              {pendingInvitations.map(invitation => (
                <div key={invitation.id} className="invitation-card">
                  <div className="invitation-info">
                    <div className="invitation-email">
                      <FiMessageSquare />
                      <span>{invitation.email}</span>
                    </div>
                    <div className="invitation-code">
                      Code: <strong>{invitation.inviteCode}</strong>
                    </div>
                    <div className="invitation-date">
                      Sent {invitation.createdAt.toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="copy-link-btn"
                    onClick={() => copyInviteLink(invitation.inviteCode)}
                  >
                    Copy Link
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State or Client Grid */}
        {clients.length === 0 ? (
          <div className="empty-state">
            <FiUsers style={{ fontSize: '4rem', color: '#ff6b35', marginBottom: '1rem' }} />
            <h3>No Clients Yet</h3>
            <p>You haven't added any clients yet. Start by inviting clients to join your coaching program.</p>
            <button className="add-btn" onClick={() => setShowInviteModal(true)}>
              <FiPlus />
              Invite Your First Client
            </button>
          </div>
        ) : (
          <>
            {/* Clients Grid */}
            <div className="clients-grid-modern">{renderClientCards()}</div>

            {/* Selected Client Details */}
            {selectedClient && renderClientDetails()}
          </>
        )}
      </div>
    );
  };

  const renderClientCards = () => {
    return clients.map(client => {
      const progress = getClientProgress(client.id);
      return (
        <div
          key={client.id}
          className={`client-card-modern ${selectedClient?.id === client.id ? 'selected' : ''}`}
          onClick={() => setSelectedClient(client)}
        >
          <div className="card-header">
            <div className="client-avatar-modern">{client.avatar}</div>
            <div className="client-info-modern">
              <h4>{client.name}</h4>
              <span className={`status-badge ${client.status}`}>{client.status}</span>
            </div>
          </div>

          <div className="card-stats">
            <div className="stat-item">
              <label>Email</label>
              <span>{client.email}</span>
            </div>
            <div className="stat-item">
              <label>Phone</label>
              <span>{client.phone}</span>
            </div>
            <div className="stat-item">
              <label>Goals</label>
              <span>{Array.isArray(client.goals) && client.goals.length > 0 ? client.goals[0] : 'Not set'}</span>
            </div>
          </div>

          <div className="card-actions">
            <button className="action-btn" onClick={(e) => { e.stopPropagation(); }}>
              <FiEye /> View
            </button>
            <button className="action-btn" onClick={(e) => { e.stopPropagation(); }}>
              <FiEdit /> Edit
            </button>
          </div>
        </div>
      );
    });
  };

  const renderClientDetails = () => {
    if (!selectedClient) return null;

    return (
      <div className="client-details-modern">
        <div className="details-header">
          <h3>{selectedClient.name}</h3>
          <button className="close-details" onClick={() => setSelectedClient(null)}>
            <FiX />
          </button>
        </div>

        <div className="details-content">
          <div className="details-section">
            <h4>Contact Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Email</label>
                <span>{selectedClient.email}</span>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <span>{selectedClient.phone}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-badge ${selectedClient.status}`}>{selectedClient.status}</span>
              </div>
            </div>
          </div>

          <div className="details-section">
            <h4>Goals & Progress</h4>
            {Array.isArray(selectedClient.goals) && selectedClient.goals.length > 0 ? (
              <ul className="goals-list">
                {selectedClient.goals.map((goal, index) => (
                  <li key={index}>
                    <FiTarget /> {goal}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-data">No goals set yet</p>
            )}
          </div>

          <div className="details-section">
            <h4>Trainer Notes</h4>
            <textarea
              className="notes-textarea"
              placeholder="Add notes about this client..."
              rows="4"
            />
            <button className="save-notes-btn">Save Notes</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCalendar = () => (
    <Calendar
      currentMonth={currentMonth}
      calendarEntries={calendarEntries}
      clients={clients}
      newEvent={newEvent}
      showEventModal={showEventModal}
      eventSaving={eventSaving}
      setShowEventModal={setShowEventModal}
      setNewEvent={setNewEvent}
      handlePreviousMonth={handlePreviousMonth}
      handleNextMonth={handleNextMonth}
      handleDateClick={handleDateClick}
      handleCreateEvent={handleCreateEvent}
    />
  );

  const renderPrograms = () => <Programs />;

  const renderFeedback = () => (
    <div className="feedback-section">
      <h2>Client Feedback</h2>
      <div className="feedback-list">
        {clientFeedback.length === 0 ? (
          <div className="empty-state">
            <FiMessageSquare style={{ fontSize: '4rem', color: '#ff6b35', marginBottom: '1rem' }} />
            <h3>No Feedback Yet</h3>
            <p>You haven't received any client feedback yet. Feedback will appear here once clients submit reviews.</p>
          </div>
        ) : (
          clientFeedback.map(feedback => (
            <div key={feedback.id} className={`feedback-card ${feedback.type}`}>
              <div className="feedback-header">
                <div className="client-info">
                  <span className="client-avatar">👤</span>
                  <div>
                    <strong>{feedback.clientName}</strong>
                    <p className="feedback-date">{feedback.date}</p>
                  </div>
                </div>
                <div className="feedback-rating">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} className={i < feedback.rating ? 'filled' : ''} />
                  ))}
                </div>
              </div>
              <div className="feedback-message">
                {feedback.message}
              </div>
              <div className="feedback-type-badge">{feedback.type}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderNutrition = () => (
    <NutritionManagement clients={clients} />
  );

  const renderActiveWidget = () => {
    switch(activeWidget) {
      case 'overview': return renderOverview();
      case 'clients': return renderClients();
      case 'calendar': return renderCalendar();
      case 'programs': return renderPrograms();
      case 'nutrition': return renderNutrition();
      case 'feedback': return <Feedback />;
      case 'analytics': return (
        <Analytics
          currentUser={currentUser}
          clients={clients}
          clientFeedback={clientFeedback}
          workoutPrograms={workoutPrograms}
          calendarEntries={calendarEntries}
        />
      );
      default: return renderOverview();
    }
  };

  return (
    <div className="trainer-dashboard">
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Enhanced Sidebar Header with Coach Profile */}
        <div className="sidebar-header-enhanced">
          <div className="coach-profile">
            {!sidebarCollapsed && (
              <>
                <div className="coach-avatar">
                  {userProfile?.photoURL ? (
                    <img src={userProfile.photoURL} alt={coachData.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {coachData.name?.charAt(0) || 'C'}
                    </div>
                  )}
                  <div className="status-indicator online"></div>
                </div>
                <div className="coach-info">
                  <h3>{coachData.name}</h3>
                  <p>Certified Trainer</p>
                </div>
              </>
            )}
          </div>
          <button
            className="collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeWidget === item.id ? 'active' : ''}`}
              onClick={() => setActiveWidget(item.id)}
            >
              <item.icon />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Quick Actions in Sidebar */}
        {!sidebarCollapsed && (
          <div className="sidebar-quick-actions">
            <h4>Quick Actions</h4>
            <div className="quick-actions-list">
              <button className="quick-action-item" onClick={() => setActiveWidget('clients')}>
                <FiPlus />
                <span>Add Client</span>
              </button>
              <button className="quick-action-item" onClick={() => setShowProgramCreator(true)}>
                <FiActivity />
                <span>Create Program</span>
              </button>
              <button className="quick-action-item" onClick={() => setActiveWidget('calendar')}>
                <FiCalendar />
                <span>Schedule Session</span>
              </button>
            </div>
          </div>
        )}

        {/* Settings at Bottom */}
        {!sidebarCollapsed && (
          <div className="sidebar-footer">
            <button className="footer-btn" onClick={() => navigate('/unique-physique/trainer-dashboard/settings')}>
              <FiSettings />
              <span>Settings</span>
            </button>
            <button className="footer-btn">
              <FiLogOut />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      <div className="main-content">
        <div className="widget-container">
          {renderActiveWidget()}
        </div>
      </div>
    </div>
  );
};

export default TrainerDashboard;